import React, { useEffect, useState, useRef, useMemo } from 'react';
import './ResizableTable.css';

interface ResizableTableProps {
    columns: Record<string, number>; // key: column name, value: width in px
    data: any[];
    onMassActionSubmit?: (selectedItems: any[]) => void; // callback for parent
    refreshButton?: boolean; // default: true
    onRefresh?: () => void; // function to call on click
    sortable?: boolean; // default: true
    sortableColumns?: string[]; // array of column keys to specify which columns are sortable
    defaultSortField?: string; // initial sort column (falls back to sortableColumns[0])
    defaultSortOrder?: 'asc' | 'desc'; // initial sort direction (default: 'desc')
    deleteButton?: boolean; // show delete button
    onDelete?: (selected: any[]) => void; // delete handler
    submitButton?: boolean; // show submit button
    onSubmit?: (selected: any[], updateValue?: string) => void; // submit handler, now receives updateValue if updatesDropdownEnabled
    submitLabel?: string; // submit button label
    updatesDropdownEnabled?: boolean; // NEW: show updates dropdown
    updatesDropdownLabel?: string; // NEW: label for updates dropdown
    updatesDropdownOptions?: string[]; // NEW: options for updates dropdown
    updatesDropdownDefault?: string; // NEW: default value for updates dropdown
    onRowClick?: (row: any) => void; // optional row click handler
}

export const ResizableTable: React.FC<ResizableTableProps> = ({
    columns,
    data,
    onMassActionSubmit,
    refreshButton = true,
    onRefresh,
    sortable = true,
    sortableColumns = [],
    defaultSortField,
    defaultSortOrder = 'desc',
    deleteButton = false,
    onDelete,
    submitButton = true,
    onSubmit,
    submitLabel = "Submit",
    updatesDropdownEnabled = false,
    updatesDropdownLabel = "Updates",
    updatesDropdownOptions = [],
    updatesDropdownDefault = "",
    onRowClick,
}) => {
    const MIN_WIDTH = 24; // allow very small columns
    const MAX_WIDTH = 600;
    const [columnWidths, setColumnWidths] = useState<number[]>([]);
    // fetch data states
    const [loading, setLoading] = useState<boolean>(false);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc' >(defaultSortOrder);
    const [error, setError] = useState<string | null>(null);
    const [sortField, setSortField] = useState<string>(
        defaultSortField ?? (sortableColumns.length > 0 ? sortableColumns[0] : '')
    );
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [massAction, setMassAction] = useState<string>('');
    // mass action states
    const [massActionLoading, setMassActionLoading] = useState(false);
    const [massActionError, setMassActionError] = useState<string | null>(null);

    // NEW: updates dropdown state
    const [updateDropdownValue, setUpdateDropdownValue] = useState<string>(updatesDropdownDefault || (updatesDropdownOptions[0] || ""));

    // Keep updateDropdownValue in sync with default/options
    useEffect(() => {
        setUpdateDropdownValue(updatesDropdownDefault || (updatesDropdownOptions[0] || ""));
    }, [updatesDropdownDefault, updatesDropdownOptions]);

    const tableRef = useRef<HTMLTableElement>(null);
    const resizingColIndex = useRef<number | null>(null);
    const startX = useRef<number>(0);
    const startWidth = useRef<number>(0);

    const setColumns = (columnsObj: Record<string, number>) => {
        // Only add checkbox as first column, no details column
        const cols = [
            { key: '__checkbox__', width: 32 },
            ...Object.entries(columnsObj).map(([key, width]) => ({ key, width }))
        ];
        setTableColumns(cols);
        setColumnWidths(cols.map(col => col.width));
    };

    const [tableColumns, setTableColumns] = useState<{ key: string, width: number }[]>([]);
    const [tableRows, setTableRows] = useState<any[]>([]);

    const setData = (items: any[]) => {
        // Each row: { ...item, __checkbox__: false }
        const rows = items.map(item => ({
            __checkbox__: false,
            ...item
        }));
        setTableRows(rows);
    };

    useEffect(() => {
        setColumns(columns);
        setData(data);
    }, [columns, data]);

    // --- Column resizing logic ---
    const handleResizeStart = (e: React.MouseEvent, index: number) => {
        if (index === 0) return;
        resizingColIndex.current = index;
        startX.current = e.clientX;
        startWidth.current = columnWidths[index];
        // Prevent text selection
        e.preventDefault();

        const handleMouseMove = (moveEvent: MouseEvent) => {
            if (resizingColIndex.current === null) return;
            const idx = resizingColIndex.current;
            if (idx === 0) return;
            const delta = moveEvent.clientX - startX.current;
            let newWidth = startWidth.current + delta;
            if (newWidth < MIN_WIDTH) newWidth = MIN_WIDTH;
            if (newWidth > MAX_WIDTH) newWidth = MAX_WIDTH;
            setColumnWidths(prev => {
                const newWidths = [...prev];
                newWidths[idx] = newWidth;
                return newWidths;
            });
        };
        const handleMouseUp = () => {
            resizingColIndex.current = null;
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };

    const handleSort = (field: string) => {
        setSortField(field);
        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    const handleSelect = (element: string) => {
        setSelectedKeys(prev => {
            const newSet = new Set(prev);
            if (newSet.has(element)) {
                newSet.delete(element);
            } else {
                newSet.add(element);
            }
            return newSet;
        });
    };

    const handleMassAction = (action: string) => {
        setMassAction(action);
    };

    const handleMassActionSubmit = () => {
        setMassActionLoading(true);
        setMassActionError(null);
        // Filter selected rows
        const selectedRows = tableRows.filter(row => row.__checkbox__);
        // Omit __checkbox__ field
        const cleaned = selectedRows.map(({ __checkbox__, ...rest }) => rest);
        if (typeof onMassActionSubmit === 'function') {
            onMassActionSubmit(cleaned);
        }
        setMassActionLoading(false);
    };

    // Update a single row's checkbox
    const handleRowCheckboxChange = (rowIdx: number) => {
        setTableRows(prevRows =>
            prevRows.map((row, idx) =>
                idx === rowIdx ? { ...row, __checkbox__: !row.__checkbox__ } : row
            )
        );
    };

    // Select/deselect all
    const allSelected = tableRows.length > 0 && tableRows.every(row => row.__checkbox__);
    const handleHeaderCheckboxChange = () => {
        if (allSelected) {
            setSelectedKeys(new Set());
        } else {
            setSelectedKeys(new Set(tableRows.map(row => row.__checkbox__)));
        }
    };

    // Define selectionKey BEFORE any usage
    const selectionKey = sortableColumns[0] || Object.keys(columns)[0] || 'id';

    // Sorted data
    const sortedData = useMemo(() => {
        if (!sortable || !sortField) return data;
        return [...data].sort((a, b) => {
            const aVal = a[sortField] || '';
            const bVal = b[sortField] || '';
            if (aVal === bVal) return 0;
            if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
            return aVal < bVal ? 1 : -1;
        });
    }, [data, sortField, sortOrder, sortable]);

    // Use selectedKeys and selectionKey for selection logic
    const selectedRows = sortedData.filter(row => selectedKeys.has(row[selectionKey]));

    // NEW: Confirm handlers
    const handleConfirm = () => {
        if (modalOpen === 'delete' && onDelete) {
            onDelete(selectedRows);
        }
        if (modalOpen === 'submit' && onSubmit) {
            if (updatesDropdownEnabled) {
                onSubmit(selectedRows, updateDropdownValue);
            } else {
                onSubmit(selectedRows);
            }
        }
        setModalOpen(null);
    };

    const [modalOpen, setModalOpen] = useState<null | 'delete' | 'submit'>(null);

    return (
        <div className="resizable-table-container">
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {refreshButton && onRefresh && (
                    <button
                        className="btn btn-primary"
                        onClick={onRefresh}
                    >
                        Refresh
                    </button>
                )}
                {sortable && sortableColumns.length > 0 && (
                    <>
                        <label htmlFor="resizable-table-sort-field">Sort by:</label>
                        <select
                            id="resizable-table-sort-field"
                            value={sortField}
                            onChange={e => setSortField(e.target.value)}
                        >
                            {sortableColumns.map(col => (
                                <option key={col} value={col}>
                                    {col}
                                </option>
                            ))}
                        </select>
                        <label htmlFor="resizable-table-sort-order">Order:</label>
                        <select
                            id="resizable-table-sort-order"
                            value={sortOrder}
                            onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')}
                        >
                            <option value="asc">Ascending</option>
                            <option value="desc">Descending</option>
                        </select>
                    </>
                )}
                {updatesDropdownEnabled && (
                    <>
                        <label htmlFor="resizable-table-updates-dropdown">{updatesDropdownLabel}:</label>
                        <select
                            id="resizable-table-updates-dropdown"
                            value={updateDropdownValue}
                            onChange={e => setUpdateDropdownValue(e.target.value)}
                            disabled={!updatesDropdownEnabled}
                            style={{ minWidth: 120 }}
                        >
                            {updatesDropdownOptions.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </>
                )}
                {submitButton && onSubmit && (
                    <button
                        className="btn btn-success"
                        onClick={() => setModalOpen('submit')}
                        disabled={selectedRows.length === 0}
                    >
                        {submitLabel}
                    </button>
                )}
                {deleteButton && onDelete && (
                    <button
                        className="btn btn-danger"
                        onClick={() => setModalOpen('delete')}
                        disabled={selectedRows.length === 0}
                    >
                        Delete
                    </button>
                )}
            </div>
            <div className="resizable-table-scroll">
            <table ref={tableRef}>
                <thead>
                    <tr>
                        {/* Checkbox column */}
                        <th style={{ width: columnWidths[0] || 32, position: 'relative' }}>
                            <input
                                type="checkbox"
                                checked={sortedData.length > 0 && sortedData.every(row => selectedKeys.has(row[selectionKey]))}
                                onChange={() => {
                                    if (sortedData.every(row => selectedKeys.has(row[selectionKey]))) {
                                        setSelectedKeys(new Set());
                                    } else {
                                        setSelectedKeys(new Set(sortedData.map(row => row[selectionKey])));
                                    }
                                }}
                            />
                        </th>
                        {Object.keys(columns).map((colKey, idx) => {
                            const colIndex = idx + 1; // +1 for checkbox column
                            return (
                                <th
                                    key={colKey}
                                    style={{ width: columnWidths[colIndex] || columns[colKey], position: 'relative', userSelect: 'none' }}
                                >
                                    <span style={{ paddingRight: 8 }}>{colKey}</span>
                                    {/* Resizer handle: not for last column, not for checkbox */}
                                    {colIndex > 0 && colIndex < columnWidths.length && (
                                        <span
                                            className="resizable-table-resizer"
                                            style={{
                                                position: 'absolute',
                                                right: 0,
                                                top: 0,
                                                height: '100%',
                                                width: 8,
                                                cursor: 'col-resize',
                                                zIndex: 2,
                                                userSelect: 'none',
                                            }}
                                            onMouseDown={e => handleResizeStart(e, colIndex)}
                                            onClick={e => e.stopPropagation()}
                                        />
                                    )}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {sortedData.map((row, i) => (
                        <tr
                            key={i}
                            onClick={() => onRowClick?.(row)}
                            style={onRowClick ? { cursor: 'pointer' } : undefined}
                        >
                            {/* Checkbox column */}
                            <td style={{ width: columnWidths[0] || 32 }} onClick={e => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    checked={selectedKeys.has(row[selectionKey])}
                                    onChange={() => {
                                        setSelectedKeys(prev => {
                                            const newSet = new Set(prev);
                                            if (newSet.has(row[selectionKey])) {
                                                newSet.delete(row[selectionKey]);
                                            } else {
                                                newSet.add(row[selectionKey]);
                                            }
                                            return newSet;
                                        });
                                    }}
                                />
                            </td>
                            {/* Data columns */}
                            {Object.keys(columns).map((colKey, idx) => (
                                <td
                                    key={colKey}
                                    style={{
                                        width: columnWidths[idx + 1] || columns[colKey],
                                        maxWidth: columnWidths[idx + 1] || columns[colKey],
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={String(row[colKey])}
                                >
                                    {String(row[colKey])}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
            {/* Modal confirm */}
            {modalOpen && (
                <div className="resizable-table-modal-overlay" style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.3)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div className="resizable-table-modal" style={{
                        background: '#fff',
                        padding: 32,
                        borderRadius: 8,
                        boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
                        minWidth: 320,
                        maxWidth: 400,
                        textAlign: 'center'
                    }}>
                        <h3>
                            {modalOpen === 'delete'
                                ? `Are you sure you want to Delete? It's irreversible.`
                                : `Are you sure you want to ${submitLabel}? It's irreversible.`}
                        </h3>
                        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center', gap: 16 }}>
                            <button
                                className={modalOpen === 'delete' ? "btn btn-danger" : "btn btn-success"}
                                onClick={handleConfirm}
                            >
                                Confirm
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setModalOpen(null)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};